import { Show } from "solid-js";
import { isNil, omit } from "lodash-es";
import { Dialog } from "@ark-ui/solid";
import { IconButton } from "../../../components/button/Button";
import SimpleTable from "../../../components/table/SimpleTable";
import FormatDate from "../../../components/formatting/FormatDate";
import { useTranslation } from "../../../state/i18n";
import type { Task, TaskFulfilment } from "../../../types/Task";
import { STATUS_MAP, type FulfilmentStatus } from "../../../utils/taskUtil";
import Pill from "../../../components/pill/Pill";
import DetailModal from "../../../components/modal/DetailModal";
import TaskRecipientsCellContent from "./TaskRecipientsCellContent";
import { condition } from "../../../utils/solidUtils";

type ViewTaskModalProps = { task: Task };

export const ViewTaskModal = (props: ViewTaskModalProps) => {
  const { t } = useTranslation();

  return (
    <div class="flex max-h-160 w-146 flex-col gap-6 overflow-y-hidden p-6 shadow-elevation-light-5">
      <div>
        <Dialog.Title>
          <h3 class="text-header-lg text-primary">{t(($) => $.VIEW_TASK_MODAL_TITLE)}</h3>
        </Dialog.Title>
        <div class="absolute top-3 right-3">
          <Dialog.CloseTrigger
            asChild={(parentProps) => (
              <IconButton
                {...omit(parentProps(), "children")}
                icon="x"
                aria-label={t(($) => $.CLOSE_ARIA)}
                size="large"
                variant="tertiary"
              />
            )}
          />
        </div>
      </div>

      <SimpleTable
        data={[
          {
            label: t(($) => $.TASKS_TABLE_COLUMNS_STATUS_HEADER),
            value: (() => {
              const { label, variant } = STATUS_MAP[props.task.status];
              return <Pill label={t(label)} variant={variant} />;
            })(),
          },
          {
            label: t(($) => $.TASKS_TABLE_COLUMNS_CREATED_ON_HEADER),
            value: <FormatDate value={props.task.dates.creationDate} />,
          },
          {
            label: t(($) => $.TASKS_TABLE_COLUMNS_SCHEDULED_ON_HEADER),
            value: <FormatDate value={props.task.dates.publishDate} />,
          },
          {
            label: t(($) => $.TASKS_TABLE_COLUMNS_DUE_DATE_HEADER),
            value: <Show when={props.task.dates.informationDate}>{(dueDate) => <FormatDate value={dueDate()} />}</Show>,
          },
          {
            label: t(($) => $.TASKS_TABLE_COLUMNS_MESSAGE_HEADER),
            value: <Show when={props.task.message}>{(message) => message()}</Show>,
          },
          {
            label: t(($) => $.TASKS_TABLE_COLUMNS_SENT_TO_HEADER),
            value: <TaskRecipientsCellContent task={props.task} />,
          },
        ]}
      />
    </div>
  );
};

type ViewTaskUserModalProps = {
  username: string;
  fulfilment: TaskFulfilment | undefined;
  status: FulfilmentStatus;
};

export const ViewTaskUserModal = (props: ViewTaskUserModalProps) => {
  const { t } = useTranslation();

  return (
    <DetailModal
      title={t(($) => $.VIEW_TASK_RECIPIENT_MODAL_TITLE)}
      content={() => (
        <SimpleTable
          data={[
            { label: t(($) => $.TASKS_RECIPIENT_COLUMN_RECIPIENT), value: props.username },
            {
              label: t(($) => $.TASKS_RECIPIENT_COLUMN_STATUS),
              value: <Pill label={t(props.status.label)} variant={props.status.variant} />,
            },
            {
              label: t(($) => $.TASKS_RECIPIENT_COLUMN_SUBMISSION),
              value: (
                <Show
                  when={condition(
                    props.fulfilment?.registrationId,
                    (submissionId) => !isNil(props.fulfilment) && !isNil(submissionId),
                  )}
                >
                  {(submissionId) => submissionId().toString()}
                </Show>
              ),
            },
            {
              label: t(($) => $.TASKS_RECIPIENT_COLUMN_DATE, {
                status:
                  props.status.label === "TASKS_STATUS_PENDING"
                    ? t(($) => $.TASKS_STATUS_COMPLETED)
                    : t(props.status.label),
              }),
              value: (
                <Show when={condition(props.fulfilment?.date, (date) => !isNil(props.fulfilment) && !isNil(date))}>
                  {(date) => <FormatDate value={date()} />}
                </Show>
              ),
            },
          ]}
        />
      )}
    />
  );
};
